import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../../../../i18n';
import { BRAND_COLORS, alpha } from '../../../../../styles/theme';
import {
  InventoryLocation,
  InventoryItem,
  InventoryItemCreate,
  InventoryItemUpdate,
} from '../../../../../types/api';
import { buildLocationPath } from './inventoryTree.utils';

interface ItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: InventoryItemCreate | InventoryItemUpdate) => void;
  locations: InventoryLocation[];
  editItem?: InventoryItem | null;
  defaultLocationId?: string | null;
  t: ReturnType<typeof useTranslation>['t'];
}

export const ItemDialog: React.FC<ItemDialogProps> = ({
  open,
  onClose,
  onSave,
  locations,
  editItem,
  defaultLocationId,
  t,
}) => {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState('');
  const [locationId, setLocationId] = useState<string | ''>('');
  const [notes, setNotes] = useState('');
  const [browseParentId, setBrowseParentId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (editItem) {
        setName(editItem.name);
        setQuantity(editItem.quantity);
        setCategory(editItem.category ?? '');
        setLocationId(editItem.location_id ?? '');
        setNotes(editItem.notes ?? '');
        const loc = editItem.location_id
          ? locations.find((l) => l.id === editItem.location_id)
          : undefined;
        setBrowseParentId(loc?.parent_id ?? null);
      } else {
        setName('');
        setQuantity(1);
        setCategory('');
        setLocationId(defaultLocationId ?? '');
        setNotes('');
        const loc = defaultLocationId
          ? locations.find((l) => l.id === defaultLocationId)
          : undefined;
        setBrowseParentId(loc?.parent_id ?? null);
      }
    }
  }, [open, editItem, defaultLocationId, locations]);

  // Drill-down picker derived data
  const visibleChildren = useMemo(
    () =>
      locations
        .filter((l) => l.parent_id === browseParentId)
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [locations, browseParentId]
  );

  const browseParent = browseParentId ? locations.find((l) => l.id === browseParentId) : null;

  const hasChildrenSet = useMemo(
    () => new Set(locations.filter((l) => l.parent_id !== null).map((l) => l.parent_id as string)),
    [locations]
  );

  const handleSave = () => {
    const data: InventoryItemCreate = {
      name: name.trim(),
      quantity,
      category: category.trim() || null,
      location_id: locationId || null,
      notes: notes.trim() || null,
    };
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editItem ? t('inventory.editItem') : t('inventory.addItem')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('inventory.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label={t('inventory.quantity')}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
            fullWidth
            inputProps={{ min: 0 }}
          />
          <TextField
            label={t('inventory.category')}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          />
          {/* Drill-down location picker */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {/* Header: label + current selection + clear */}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'action.hover',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('inventory.location')}
                </Typography>
                <Typography variant="body2" noWrap>
                  {locationId
                    ? buildLocationPath(locationId, locations)
                        .map((l) => l.name)
                        .join(' > ')
                    : t('inventory.noSelection')}
                </Typography>
              </Box>
              {locationId && (
                <IconButton size="small" onClick={() => setLocationId('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Back button */}
            {browseParentId !== null && (
              <ListItemButton
                onClick={() => setBrowseParentId(browseParent?.parent_id ?? null)}
                sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ChevronRightIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('inventory.backTo', {
                    name: browseParent?.name ?? t('inventory.rootLevel'),
                  })}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            )}

            {/* Children list */}
            <List disablePadding sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {visibleChildren.length === 0 ? (
                <Typography variant="body2" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>
                  {t('inventory.noLocations')}
                </Typography>
              ) : (
                visibleChildren.map((child) => {
                  const childHasChildren = hasChildrenSet.has(child.id);
                  const isSelected = child.id === locationId;
                  return (
                    <ListItemButton
                      key={child.id}
                      selected={isSelected}
                      onClick={() => {
                        setLocationId(child.id);
                        if (childHasChildren) {
                          setBrowseParentId(child.id);
                        }
                      }}
                      sx={{
                        py: 0.5,
                        minHeight: 36,
                        '&.Mui-selected': {
                          backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.08),
                          '&:hover': {
                            backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {childHasChildren ? (
                          <ChevronRightIcon fontSize="small" />
                        ) : (
                          <Box sx={{ width: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.name}
                        secondary={t(
                          `inventory.locationTypes.${child.location_type}` as Parameters<
                            typeof t
                          >[0]
                        )}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {isSelected && (
                        <CheckIcon fontSize="small" sx={{ color: BRAND_COLORS.forestGreen.main }} />
                      )}
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Box>
          <TextField
            label={t('inventory.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!name.trim()}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
